import { DamageType } from "../damageType";
import type { Direction } from "../direction";
import { BigShockBlast } from "../effects/bigShockBlast";
import { ShockBlast } from "../effects/shockBlast";
import type { ParticleEmitter } from "../interfaces";
import { ParticleEffect } from "../effects/particleEffect";
import { ParticleType } from "../particleType";
import { PhysicalThingWithHp, STD_BASE_HP } from "../physicalThingWithHp";
import { deserializeXY } from "../serialization";
import { SimpleProjectile } from "../simpleProjectile";
import { Sound } from "../things/sounds/sound";
import { SoundId } from "../things/sounds/soundId";
import type { XY } from "../xy";

const DIRECT_DAMAGE = STD_BASE_HP * 2;
const CHAIN_DAMAGE = STD_BASE_HP;

export class PlasmaBall extends SimpleProjectile implements ParticleEmitter {
	static key = "PlasmaBall";

	getParticleType(): ParticleType {
		return ParticleType.ELECTRO_TRAIL;
	}

	constructor(xy: XY, direction: Direction) {
		super(xy, direction);
	}

	protected damageTarget(target: PhysicalThingWithHp): number {
		return target.applyDamage(DamageType.ELECTRIC, DIRECT_DAMAGE, this);
	}

	onHit(damageDealt: number): void {
		this.board!.removeAtEndOfTurn(this);
		this.board!.addNonPhysicalThing(new Sound(SoundId.EXPLOSION_SHOCK, this.xy));
		this.board!.addEffect(new BigShockBlast(this.xy));
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.PLASMA_CLOUD, 30));
		this.board!.addEffect(new ParticleEffect(this.xy, ParticleType.PLASMA_SPARK, 14));

		if (damageDealt <= 0) return;

		// Chain damage to all 8 neighboring cells
		const neighbors = this.xy.getNeighbors();
		for (const neighborPos of neighbors) {
			if (!this.board!.isInBounds(neighborPos)) continue;

			const things = this.board!.getCell(neighborPos).getThings();
			const targets = things
				.filter((t) => t.active)
				.filter((t) => t instanceof PhysicalThingWithHp) as PhysicalThingWithHp[];

			for (const target of targets) {
				if (!target.active) continue;
				// RULE_WEAPONS_DAMAGE_PLAYER is hardcoded true — include player in chain damage
				target.applyDamage(DamageType.ELECTRIC, CHAIN_DAMAGE, this);
				this.board!.addEffect(new ShockBlast(neighborPos));
			}
		}
	}

	static deserialize(data: any): PlasmaBall {
		const ball = new PlasmaBall(deserializeXY(data.xy), data.direction as Direction);
		ball.visualId = data.visualId;
		return ball;
	}
}
